import { distanceToString } from "utilities/format";
import constants from "toolConstants/conferenceroomDesigner/constants";
import { values, upperCase, startCase, findKey, every } from 'lodash'

const reportDocument = {
  metadata: {
    title: 'Conference Room Designer Report',
    author: 'Biamp',
    subject: 'Conference Room Designer results report',
    creator: 'Biamp'
  },

  body: [
    {
      id: 'header',
      content: [
        {
          element: 'header',
          content: 'currentTool'
        },
        {
          element: 'headerSubTitle',
          content: 'project.name'
        },
      ]
    },
    {
      id: 'project',
      content: [
        {
          element: 'stringField',
          label: 'Design Company',
          content: 'project.company'
        },
        {
          element: 'stringField',
          label: 'Design Engineer',
          content: 'project.designer'
        },
        {
          element: 'valueField',
          label: 'Date Generated',
          content: new Date().toString().split(/\(/g)[0]
        },
        // {
        //   element: 'linkField',
        //   label: 'Design Location',
        //   content: { text: 'Click Here', },
        // }
      ]
    },
  ],

  areas: (state) => values(state.areas).reduce((result, area) => {
    result.push(
      {
        id: area.id,
        content: [
          {
            element: 'title',
            content: area.name,
          },
          {
            element: 'subTitle',
            content: `${upperCase(area.areaType)} DETAILS`,
          },
          {
            element: 'valueField',
            label: `${area.areaType} Dimensions: `,
            content: `${distanceToString(area.dimensions.length, state.isMeter)} x ${distanceToString(area.dimensions.width, state.isMeter)} x ${distanceToString(area.ceilingHeight.min, state.isMeter)}`
          },
          {
            element: 'stringField',
            label: 'Table shape: ',
            content: `${area.table.shape ?? ''}`
          },
          {
            element: 'valueField',
            label: `Table ${area.table.shape === 'round' ? 'diameter' : 'dimensions'}:`,
            content: `${area.table.shape ? `${distanceToString(area.table.size.length, state.isMeter)} ${area.table.shape !== 'round' ? `x ${distanceToString(area.table.size.width, state.isMeter)}` : ''}` : ''}`
          },
          {
            element: 'linkField',
            label: 'Acoustics: ',
            content: { src: 'http://bia.mp/RoomAcoustics', text: `${startCase(area.acoustics)}` }
          },
          {
            element: 'stringField',
            label: 'Mic Style: ',
            content: `${area.microphones.model.replace('mic', '')}`
          },
          {
            element: 'valueField',
            label: 'Mic Coverage: ',
            content: `${!every(area.microphones.coverage, coverage => !coverage) ? `${startCase(findKey(area.microphones.coverage))}` : 'N/A'}`
          },
          {
            element: 'valueField',
            label: 'Speaker Coverage: ',
            content: `${!every(area.speakers.coverage, coverage => !coverage) ? `${startCase(findKey(area.speakers.coverage))}` : 'N/A'}`
          }
        ]
      },
      {
        id: `${area.id}-microphones`,
        content: [
          {
            element: 'subTitle',
            content: 'MICROPHONE COVERAGE'
          },
          {
            element: 'plot',
            areaId: area.id,
            content: (state) => state,
            device: 'microphones',
            constants,
          },

        ]
      },
      {
        id: 'micPositioningDetail',
        wrap: true,
        content: [
          {
            element: 'subTitle',
            content: (state) => {
              const micModel = state.areas[area.id].microphones.model.replace('mic', '_mic');
              const { isBarMic = false } = constants.equipment.microphones[micModel];
              return `${isBarMic ? 'CONFERENCE BAR' : 'MICROPHONE'} POSITIONING DETAIL`
            }
          },
          {
            element: 'table',
            content: (state) => {
              const micModel = state.areas[area.id].microphones.model.replace('mic', '_mic');
              const { isBarMic = false } = constants.equipment.microphones[micModel];
              return { listData: state.areas[area.id].microphones.layout, isMeter: state.isMeter, adjust: isBarMic, dimensions: state.areas[area.id].dimensions }
            },
            constants: constants.equipmentLists.microphonePositioning
          }
        ]
      },
      {
        id: `${area.id}-speakers`,
        content: [
          {
            element: 'subTitle',
            content: 'SPEAKER COVERAGE'
          },
          {
            element: 'plot',
            areaId: area.id,
            content: (state) => state,
            device: 'speakers',
            constants,
          },
        ]
      },
      {
        id: 'spkrPositioningDetail',
        wrap: true,
        content: [
          {
            element: 'subTitle',
            content: (state) => {
              const spkrModel = state.areas[area.id].speakers.model;
              const { isBarSpkr = false } = constants.equipment.speakers[spkrModel];
              return `${isBarSpkr ? 'CONFERENCE BAR' : 'SPEAKER'} POSITIONING DETAIL`
            }
          },
          {
            element: 'table',
            content: (state) => {
              const spkrModel = state.areas[area.id].speakers.model;
              const { isBarSpkr = false } = constants.equipment.speakers[spkrModel]
              return { listData: state.areas[area.id].speakers.layout, isMeter: state.isMeter, adjust: isBarSpkr, dimensions: state.areas[area.id].dimensions }
            },
            constants: constants.equipmentLists.speakerPositioning
          }
        ]
      },
    );
    // Check if area has a camera, if not bypass this section 
    if (area.cameras.hasCamera) {
      result.push(
        {
          id: `${area.id}-cameras`,
          content: [
            {
              element: 'subTitle',
              content: 'CAMERA COVERAGE'
            },
            {
              element: 'plot',
              areaId: area.id,
              content: (state) => state,
              device: 'cameras',
              constants,
            },
          ]
        },
        {
          id: 'cameraPositioningDetail',
          wrap: true,
          content: [
            {
              element: 'subTitle',
              content: (state) => {
                const cameraModel = state.areas[area.id].cameras.model;
                const { isBarCamera = false } = constants.equipment.cameras[cameraModel];
                return `${isBarCamera ? 'CONFERENCE BAR' : 'CAMERA'} POSITIONING DETAIL`
              }
            },
            {
              element: 'table',
              content: (state) => {
                const dimensions = state.areas[area.id].dimensions;
                const { shape, transform: tableCenter } = state.areas[area.id].table;
                const center = Boolean(shape) ? tableCenter : {};
                return { listData: state.areas[area.id].cameras.layout, isMeter: state.isMeter, adjust: true, dimensions, center }
              },
              constants: constants.equipmentLists.cameraPositioning
            }
          ]
        },
      );

    };
    result.push(
      {
        id: 'equipmentList',
        wrap: true,
        content: [
          {
            element: 'subTitle',
            content: 'EQUIPMENT LIST'
          },
          {
            element: 'table',
            content: (state) => ({ listData: state.areas[area.id].equipmentList }),
            constants: constants.equipmentLists.summary
          }
        ]
      },
    )
    return result
  }, []),
};

export default reportDocument;